import { createCn } from "cn/config";

/**
 * Type roles from design.md. Registered as font-size so `cn("text-body-sm",
 * "text-foreground")` keeps both — an unregistered `text-*` name is treated
 * as a text color and the size is dropped.
 */
const TYPE_ROLES = [
	"display-xl",
	"display-lg",
	"display-md",
	"headline",
	"card-title",
	"subhead",
	"body-lg",
	"body",
	"body-sm",
	"caption",
	"button",
	"eyebrow",
	"meta",
	"mono",
] as const;

export const cn = createCn({
	extend: {
		classGroups: {
			"font-size": [{ text: [...TYPE_ROLES] }],
		},
	},
});

/**
 * Human byte size in binary (1024) units: 512 → "512 B", 1536 → "1.5 KB",
 * 3_145_728 → "3.0 MB". The shared formatter behind the uploads / crm / kb
 * file-size labels.
 */
export function formatBytes(n: number): string {
	if (n < 1024) return `${n} B`;
	if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
	return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Seconds → a `m:ss` clock string: 5 → "0:05", 83 → "1:23". For elapsed-time /
 * recording-duration timers. Floors both components; seconds zero-padded to 2.
 */
export function formatClock(seconds: number): string {
	const m = Math.floor(seconds / 60);
	const s = Math.floor(seconds % 60);
	return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Compact relative time from an ISO timestamp: "just now", "4m ago", "2h ago",
 * "3d ago", "5mo ago", "2y ago". Returns "" for a nullish/invalid input. `now`
 * is injectable for testing. The shared formatter behind kb's `timeAgo` and
 * crm's `formatRelativeTime`.
 */
export function timeAgo(iso: string | null | undefined, now: Date = new Date()): string {
	if (!iso) return "";
	const then = new Date(iso).getTime();
	if (Number.isNaN(then)) return "";
	const secs = Math.max(0, Math.floor((now.getTime() - then) / 1000));
	if (secs < 60) return "just now";
	const mins = Math.floor(secs / 60);
	if (mins < 60) return `${mins}m ago`;
	const hrs = Math.floor(mins / 60);
	if (hrs < 24) return `${hrs}h ago`;
	const days = Math.floor(hrs / 24);
	if (days < 30) return `${days}d ago`;
	const months = Math.floor(days / 30);
	if (months < 12) return `${months}mo ago`;
	return `${Math.floor(months / 12)}y ago`;
}
